import React from 'react';
import ReactDOM from 'react-dom';
import { t } from '../../services/HelperService';

const MIN_SIZE = 8;
const DRAG_THRESHOLD = 3;
// Above the Kendo dialog stack, so the overlay covers the dialog, its backdrop and the page behind it.
const OVERLAY_Z = 100000;

const clamp = (v, lo, hi) => Math.min(hi, Math.max(lo, v));

const normalizeRect = (a, b) => ({
  x: Math.min(a.x, b.x),
  y: Math.min(a.y, b.y),
  w: Math.abs(a.x - b.x),
  h: Math.abs(a.y - b.y),
});

/** Whether the browser can hand us the screen's pixels (HTTPS or localhost, a desktop browser). */
export const supportsScreenSnip = () => {
  try {
    return typeof navigator !== 'undefined' && !!navigator.mediaDevices && typeof navigator.mediaDevices.getDisplayMedia === 'function';
  } catch {
    return false;
  }
};

/** The user closed the browser's share picker without choosing — not an error. */
export const isSnipCancelled = (err) => err?.name === 'NotAllowedError' || err?.name === 'AbortError';

const nextPaintedFrame = (video) => new Promise((resolve) => {
  if (typeof video.requestVideoFrameCallback === 'function') {
    video.requestVideoFrameCallback(() => resolve());
    return;
  }
  // No frame callback: a short wait past the first paint keeps a black first frame out.
  setTimeout(resolve, 150);
});

/**
 * One frozen frame of what the user sees, as a canvas: the browser's share picker
 * (the current tab preselected where the browser allows) → one video frame → the
 * track stopped at once, so the "sharing" bar is gone before the crop begins. The
 * frame is the real pixels — iframes, PDF canvases, charts and the page behind the
 * dialog included — which the DOM re-painters (html2canvas, kendo-drawing) cannot see.
 * Resolves null when the user cancels the picker.
 */
export const captureScreenFrame = async () => {
  if (!supportsScreenSnip()) throw new Error(t('This browser cannot capture the screen.'));
  let stream;
  try {
    stream = await navigator.mediaDevices.getDisplayMedia({
      video: { displaySurface: 'browser', frameRate: 5 },
      audio: false,
      preferCurrentTab: true,
      selfBrowserSurface: 'include',
      surfaceSwitching: 'exclude',
      systemAudio: 'exclude',
    });
  } catch (err) {
    if (isSnipCancelled(err)) return null;
    throw err;
  }
  const video = document.createElement('video');
  try {
    video.muted = true;
    video.playsInline = true;
    video.srcObject = stream;
    await video.play();
    if (!video.videoWidth) {
      await new Promise((resolve) => { video.onloadedmetadata = () => resolve(); setTimeout(resolve, 1500); });
    }
    await nextPaintedFrame(video);
    const canvas = document.createElement('canvas');
    canvas.width = video.videoWidth || 1;
    canvas.height = video.videoHeight || 1;
    canvas.getContext('2d').drawImage(video, 0, 0);
    return canvas;
  } finally {
    try { video.pause(); } catch { /* fine */ }
    video.srcObject = null;
    stream.getTracks().forEach((track) => { try { track.stop(); } catch { /* fine */ } });
  }
};

/** The part of <paramref name="frame"/> under a rectangle in frame pixels, as its own canvas. */
export const cropFrame = (frame, rect) => {
  const x = clamp(Math.round(rect.x), 0, frame.width - 1);
  const y = clamp(Math.round(rect.y), 0, frame.height - 1);
  const w = clamp(Math.round(rect.w), 1, frame.width - x);
  const h = clamp(Math.round(rect.h), 1, frame.height - y);
  const canvas = document.createElement('canvas');
  canvas.width = w;
  canvas.height = h;
  canvas.getContext('2d').drawImage(frame, x, y, w, h, 0, 0, w, h);
  return canvas;
};

/**
 * The crop over a captured frame: the frame fills the viewport (letterboxed), the
 * user drags a marquee, Escape or a click without a drag cancels. The rectangle is
 * cropped in FRAME pixels, so a window or whole-screen capture crops as truly as the
 * tab's. Portaled to the body, above every dialog.
 *
 * Props: frame (canvas), onCrop(canvas), onCancel()
 */
export const ScreenSnipOverlay = ({ frame, onCrop, onCancel }) => {
  const hostRef = React.useRef(null);
  const imageRef = React.useRef(null);
  const dragRef = React.useRef(null);
  const [rect, setRect] = React.useState(null);
  const [frameUrl, setFrameUrl] = React.useState('');

  React.useEffect(() => {
    if (!frame) return undefined;
    let url = '';
    try { url = frame.toDataURL('image/png'); } catch { url = ''; }
    setFrameUrl(url);
    return () => setFrameUrl('');
  }, [frame]);

  React.useEffect(() => {
    const onKey = (e) => {
      if (e.key !== 'Escape') return;
      e.preventDefault();
      e.stopPropagation();
      dragRef.current = null;
      setRect(null);
      onCancel?.();
    };
    window.addEventListener('keydown', onKey, true);
    return () => window.removeEventListener('keydown', onKey, true);
  }, [onCancel]);

  // The frame's box on screen (object-fit: contain inside the viewport) — what pointer
  // coordinates are clamped to and mapped from.
  const imageBox = () => {
    const img = imageRef.current;
    if (!img) return null;
    const b = img.getBoundingClientRect();
    return { left: b.left, top: b.top, width: b.width, height: b.height };
  };

  const pointInImage = (e) => {
    const box = imageBox();
    if (!box) return { x: 0, y: 0 };
    return { x: clamp(e.clientX - box.left, 0, box.width), y: clamp(e.clientY - box.top, 0, box.height) };
  };

  const onMouseDown = (e) => {
    if (e.button !== 0 || !frame) return;
    e.preventDefault();
    e.stopPropagation();
    const start = pointInImage(e);
    dragRef.current = { start, moved: false };
    setRect({ ...start, w: 0, h: 0 });

    const cleanup = () => {
      window.removeEventListener('mousemove', onMove, true);
      window.removeEventListener('mouseup', onUp, true);
    };
    const onMove = (ev) => {
      const d = dragRef.current;
      if (!d) return;
      const p = pointInImage(ev);
      if (Math.abs(p.x - d.start.x) > DRAG_THRESHOLD || Math.abs(p.y - d.start.y) > DRAG_THRESHOLD) d.moved = true;
      setRect(normalizeRect(d.start, p));
    };
    const onUp = (ev) => {
      cleanup();
      const d = dragRef.current;
      dragRef.current = null;
      setRect(null);
      if (!d) return;
      const r = normalizeRect(d.start, pointInImage(ev));
      const box = imageBox();
      if (!d.moved || !box || r.w < MIN_SIZE || r.h < MIN_SIZE) {
        onCancel?.();
        return;
      }
      ev.preventDefault();
      const scaleX = frame.width / box.width;
      const scaleY = frame.height / box.height;
      onCrop?.(cropFrame(frame, { x: r.x * scaleX, y: r.y * scaleY, w: r.w * scaleX, h: r.h * scaleY }));
    };
    window.addEventListener('mousemove', onMove, true);
    window.addEventListener('mouseup', onUp, true);
  };

  if (!frame || typeof document === 'undefined') return null;
  const box = imageBox();

  return ReactDOM.createPortal(
    <div
      ref={hostRef}
      data-tagname="screenSnip"
      onMouseDown={onMouseDown}
      onContextMenu={(e) => e.preventDefault()}
      style={{
        position: 'fixed', inset: 0, zIndex: OVERLAY_Z, background: '#111', cursor: 'crosshair', userSelect: 'none',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
      }}
    >
      {frameUrl && (
        <img
          ref={imageRef}
          src={frameUrl}
          alt=""
          draggable={false}
          style={{ maxWidth: '100vw', maxHeight: '100vh', width: 'auto', height: 'auto', display: 'block', pointerEvents: 'none' }}
        />
      )}
      {!rect && (
        <div
          style={{
            position: 'absolute', top: 12, left: '50%', transform: 'translateX(-50%)',
            background: 'rgba(111, 66, 193, 0.92)', color: '#fff', fontSize: 13, padding: '5px 12px', borderRadius: 14,
            pointerEvents: 'none', whiteSpace: 'nowrap', boxShadow: '0 2px 8px rgba(0,0,0,0.35)',
          }}
        >
          {t('Drag over the part to send with your message (Esc to cancel)')}
        </div>
      )}
      {rect && box && (
        <div
          style={{
            position: 'fixed', pointerEvents: 'none',
            left: box.left + rect.x, top: box.top + rect.y, width: rect.w, height: rect.h,
            border: '2px solid #6f42c1', background: 'rgba(111, 66, 193, 0.10)',
            boxShadow: '0 0 0 9999px rgba(0, 0, 0, 0.45)',
          }}
        />
      )}
    </div>,
    document.body,
  );
};

/**
 * The snip from a button: capture the screen, then crop it. Returns { supported,
 * busy, start, overlay } — the host renders `overlay` (null when idle) and calls
 * `start()` from its camera button. onCapture(canvas) gets the cropped pixels;
 * onError(err) a failure other than the user cancelling the picker.
 */
export const useScreenSnip = ({ onCapture, onError } = {}) => {
  const [frame, setFrame] = React.useState(null);
  const [busy, setBusy] = React.useState(false);
  const onCaptureRef = React.useRef(onCapture);
  onCaptureRef.current = onCapture;
  const onErrorRef = React.useRef(onError);
  onErrorRef.current = onError;

  const start = React.useCallback(async () => {
    if (busy || frame) return;
    setBusy(true);
    try {
      const captured = await captureScreenFrame();
      if (captured) setFrame(captured);
    } catch (err) {
      try { onErrorRef.current?.(err); } catch { /* the host's own reporting */ }
    } finally {
      setBusy(false);
    }
  }, [busy, frame]);

  const cancel = React.useCallback(() => setFrame(null), []);
  const crop = React.useCallback((canvas) => {
    setFrame(null);
    try { onCaptureRef.current?.(canvas); } catch (err) { try { onErrorRef.current?.(err); } catch { /* fine */ } }
  }, []);

  const overlay = frame ? <ScreenSnipOverlay frame={frame} onCrop={crop} onCancel={cancel} /> : null;
  return { supported: supportsScreenSnip(), busy, start, overlay };
};
